import { useState } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';

import { Endpoints } from '@/api';
import FileUpload from '@/components/FileUpload';
import InputQuantity from '@/components/InputQuantity/InputQuantity';
import Button from '@/design_system/Button';
import { Label } from '@/design_system/Label/Label';
import { OptionalLabel } from '@/design_system/Label/OptionalLabel';
import { RadioCard, RadioCardGroup } from '@/design_system/Radio/RadioCard/RadioCard';
import Stack from '@/design_system/Stack';
import TextArea from '@/design_system/TextArea';
import Paragraph from '@/design_system/Typography/Paragraph';
import { useBrandRequestContext } from '@/layouts/Brand/BrandRequestContext';
import { hasDefects, useCreateDefect, useUpdateDefect } from '@/models/article';
import { DEFECT_ICONS_BY_ID, useDefectLevelValues } from '@/models/defectLevelValue';
import { DefectTypeFromGetMany, useDefectTypes } from '@/models/defectType';
import { Medium, useDeleteMedium } from '@/models/medium';
import { ArticleDefectWithRelations, ArticleWithRelations } from '@/models/request';
import ArticleCard from '@/routes/Brand/Requests/New/components/Article/components/ArticleCard';
import { ProgressBar } from '@/routes/Brand/Requests/New/components/ProgressBar/ProgressBar';
import { useDatabaseTranslation } from '@/services/i18n';
import { createBEMClasses } from '@/utils/classname';
import useViewPort from '@/utils/useViewport';

import './DefectsForm.css';

const { block, element } = createBEMClasses('client-defects-form');

type DefectLevelValue = Endpoints['GET /defect-level-values']['response'][number];

type DefectFormProps = {
  initialDefects: ArticleDefectWithRelations[];
  article: ArticleWithRelations;
  onEditArticle: () => void;
  onDeleteArticle: () => void;
  onSaveDefects: () => void;
  onCancel: () => void;
};

const DefectsFormWrapper = (props: DefectFormProps) => {
  const { data: defectTypes } = useDefectTypes({ articleId: props.article.id });
  const { data: defectLevelValues } = useDefectLevelValues({ articleId: props.article.id });

  if (!defectTypes || !defectLevelValues) {
    return null;
  }

  return <DefectsForm {...props} defectTypes={defectTypes} defectLevelValues={defectLevelValues} />;
};

const DefectsForm = ({
  initialDefects,
  article,
  onEditArticle,
  onDeleteArticle,
  onSaveDefects,
  onCancel,
  defectTypes,
  defectLevelValues,
}: DefectFormProps & {
  defectTypes: DefectTypeFromGetMany[];
  defectLevelValues: DefectLevelValue[];
}) => {
  const { request, view } = useBrandRequestContext();

  const initialDefectData = initialDefects[0] as ArticleDefectWithRelations | undefined;

  const { t } = useLingui();
  const { _db } = useDatabaseTranslation();
  const { isMobile } = useViewPort();

  const [selectedDefectLevelValues, setSelectedDefectLevelValues] = useState<
    (DefectLevelValue | 'custom' | null)[]
  >(getInitialDefectLevelValues({ initialDefectData, defectLevelValues }));

  const lastSelectedDefectLevelValue = selectedDefectLevelValues.at(-1) ?? null;

  const selectedDefectType =
    !!lastSelectedDefectLevelValue && lastSelectedDefectLevelValue !== 'custom'
      ? defectTypes.find((defectType) =>
          lastSelectedDefectLevelValue.defectTypeOrganizationIds.includes(defectType.id)
        )
      : undefined;

  const [defectPhotosByDefect, setDefectPhotosByDefect] = useState<Medium[][]>(
    initialDefects.length ? initialDefects.map((defect) => defect.media) : [[]]
  );

  const [contextByDefect, setContextByDefect] = useState<string[]>(
    initialDefects.length ? initialDefects.map((defect) => defect.context ?? '') : ['']
  );

  const [customDefectDescription, setCustomDefectDescription] = useState(
    initialDefectData?.customDescription ?? ''
  );
  const trimmedCustomDefectDescription = customDefectDescription.trim();

  const [quantity, setQuantity] = useState(initialDefects.length ? initialDefects.length : 1);

  const [mediaToDelete, setMediaToDelete] = useState<string[]>([]);
  const { mutate: deleteMedium } = useDeleteMedium();

  const handleSetQuantity = (newQuantity: number) => {
    if (newQuantity > defectPhotosByDefect.length) {
      setDefectPhotosByDefect([...defectPhotosByDefect, []]);
      setContextByDefect([...contextByDefect, '']);
    }

    setQuantity(newQuantity);
  };

  const areDefectPhotosValid =
    !view.articles.defects.photo.required ||
    (defectPhotosByDefect.length >= quantity &&
      defectPhotosByDefect.slice(0, quantity).every((defectPhotos) => defectPhotos.length > 0));

  const areContextsValid =
    !view.articles.defects.context.required ||
    (contextByDefect.length >= quantity &&
      contextByDefect.slice(0, quantity).every((context) => !!context));

  const disableSave =
    (!selectedDefectType && !trimmedCustomDefectDescription) ||
    !areDefectPhotosValid ||
    !areContextsValid;

  const { mutateAsync: createDefect, isPending: isCreateDefectPending } = useCreateDefect({
    articleId: article.id,
    requestId: request.id,
  });

  const { mutateAsync: updateDefect, isPending: isUpdateDefectPending } = useUpdateDefect({
    articleId: article.id,
    requestId: request.id,
  });

  const saveDefects = async () => {
    if (disableSave) {
      return;
    }

    if (selectedDefectType) {
      for (let i = 0; i < quantity; i++) {
        const existingDefect = initialDefects[i];

        if (existingDefect) {
          await updateDefect({
            defectId: existingDefect.id,
            body: {
              defectTypeOrganizationId: selectedDefectType.id,
              defectPhotoIds: defectPhotosByDefect[i]?.map(({ id }) => id) ?? [],
              context: contextByDefect[i] ?? null,
            },
          });
        } else {
          await createDefect({
            defectTypeOrganization: selectedDefectType,
            defectPhotoIds: defectPhotosByDefect[i]?.map(({ id }) => id) ?? [],
            context: contextByDefect[i] ?? null,
          });
        }
      }
    } else if (trimmedCustomDefectDescription) {
      for (let i = 0; i < quantity; i++) {
        const existingDefect = initialDefects[i];

        if (existingDefect) {
          await updateDefect({
            defectId: existingDefect.id,
            body: {
              description: trimmedCustomDefectDescription,
              defectPhotoIds: defectPhotosByDefect[i]?.map(({ id }) => id) ?? [],
              context: contextByDefect[i] ?? null,
            },
          });
        } else {
          await createDefect({
            description: trimmedCustomDefectDescription,
            defectPhotoIds: defectPhotosByDefect[i]?.map(({ id }) => id) ?? [],
            context: contextByDefect[i] ?? null,
          });
        }
      }
    }

    const extraMediaToDelete = defectPhotosByDefect
      .slice(quantity)
      .flatMap((media) => media.map(({ id }) => id));

    [...mediaToDelete, ...extraMediaToDelete].forEach((id) => deleteMedium(id));
    onSaveDefects();
  };

  const discard = () => {
    const initialDefectPhotoIds = initialDefects.flatMap((defect) =>
      defect.media.map(({ id }) => id)
    );
    const currentDefectPhotoIds = defectPhotosByDefect.flatMap((media) =>
      media.map(({ id }) => id)
    );

    currentDefectPhotoIds
      .filter((id) => !initialDefectPhotoIds.includes(id))
      .forEach((id) => deleteMedium(id));

    onCancel();
  };

  let progress = Math.min(80, 50 + selectedDefectLevelValues.length * 5);

  if (selectedDefectType || !!trimmedCustomDefectDescription) {
    progress = 85;

    if (areDefectPhotosValid && areContextsValid) {
      progress = 95;

      if (isCreateDefectPending || isUpdateDefectPending) {
        progress = 100;
      }
    }
  }

  return (
    <div className={block()}>
      <main>
        <div className={element('article')}>
          <ArticleCard article={article} onEdit={onEditArticle} onDelete={onDeleteArticle} />
        </div>
        <Stack className={element('defects')} gap="3rem">
          {selectedDefectLevelValues.map((selectedDefectLevelValue, index) => {
            let label: string = '';
            let options: (DefectLevelValue | 'custom')[] = [];

            if (index === 0) {
              if (initialDefectData && selectedDefectLevelValue === 'custom') {
                // When editing a custom defect, it cannot be changed into a catalog defect
                options = ['custom'];
              } else {
                options = [...defectLevelValues.filter((dlv) => !dlv.parentId)];

                if (view.articles.defects.customEnabled && !initialDefectData) {
                  // When editing a catalog defect, it cannot be changed into a custom defect
                  options.push('custom');
                }
              }

              label = t({
                id: 'action-type.category.repair.next',
                message: 'What needs to be repaired?',
              });
            } else {
              const selectedParent = index === 0 ? undefined : selectedDefectLevelValues[index - 1];

              if (!selectedParent || selectedParent === 'custom') {
                return null;
              }

              options = defectLevelValues.filter((dlv) => dlv.parentId === selectedParent.id);

              label = selectedParent.nextLevelLabel
                ? _db(selectedParent.nextLevelLabel)
                : t({
                    id: 'client.new.actions.form.open-question',
                    message: 'Could you specify?',
                  });
            }

            const selectedOptionId = selectedDefectLevelValue
              ? selectedDefectLevelValue === 'custom'
                ? 'custom'
                : selectedDefectLevelValue.id
              : null;

            return (
              <Label label={label} key={index} size="large">
                <RadioCardGroup
                  name={`defect-level-value-${index}`}
                  theme="brand"
                  direction="column"
                  value={selectedOptionId}
                  onChange={(newSelectedDefectLevelValueId) => {
                    if (newSelectedDefectLevelValueId === selectedOptionId) {
                      return;
                    } else if (newSelectedDefectLevelValueId === 'custom') {
                      setSelectedDefectLevelValues(['custom']);
                    } else {
                      const newSelectedDefectLevelValue = defectLevelValues.find(
                        (dlv) => dlv.id === newSelectedDefectLevelValueId
                      );

                      if (newSelectedDefectLevelValue) {
                        const newSelectedDefectLevelValues = [
                          ...selectedDefectLevelValues.slice(0, index),
                          newSelectedDefectLevelValue,
                        ];

                        if (!newSelectedDefectLevelValue.defectTypeOrganizationIds[0]) {
                          // Add null to show the next level to be selected
                          newSelectedDefectLevelValues.push(null);
                        }

                        setSelectedDefectLevelValues(newSelectedDefectLevelValues);
                      }
                    }
                  }}
                  scrollToOnMount
                >
                  {options.map((option) =>
                    option === 'custom' ? (
                      <RadioCard
                        key="custom"
                        value="custom"
                        ariaLabel={t({
                          id: 'client.new.actions.form.custom-action.text',
                          message: 'A doubt?',
                        })}
                      >
                        {t({
                          id: 'client.new.actions.form.custom-action.text',
                          message: 'A doubt?',
                        })}
                      </RadioCard>
                    ) : (
                      <RadioCard
                        key={option.id}
                        value={option.id}
                        ariaLabel={_db(option.name)}
                        picture={
                          option.illustration?.url ? (
                            <img src={option.illustration.url} alt="" />
                          ) : option.illustration?.icon ? (
                            DEFECT_ICONS_BY_ID[option.illustration.icon]
                          ) : option.illustration?.backgroundColor ? (
                            <div
                              style={{
                                backgroundImage: `url("${option.illustration.backgroundUrl}")`,
                                backgroundSize: 'cover',
                                height: '100%',
                                width: '100%',
                              }}
                            />
                          ) : undefined
                        }
                        backgroundColor={option.illustration?.backgroundColor}
                      >
                        {_db(option.name)}
                      </RadioCard>
                    )
                  )}
                </RadioCardGroup>
              </Label>
            );
          })}

          {lastSelectedDefectLevelValue === 'custom' && (
            <TextArea
              label={t({
                id: 'client.new.actions.form.custom-action.label',
                message: "Describe the item's issues",
              })}
              value={customDefectDescription}
              onChange={(e) => setCustomDefectDescription(e.target.value)}
              rows={6}
              size="large"
              scrollToAndFocusOnRender
            />
          )}

          {selectedDefectType?.isQuantifiable && (
            <Stack gap="0.5rem">
              <Paragraph size="50" weight="medium">
                {t({
                  id: 'client.new.actions.form.quantity-question',
                  message: 'Please specify the quantity of this defect',
                })}
              </Paragraph>
              <InputQuantity
                quantity={quantity}
                onDecrement={() => handleSetQuantity(quantity - 1)}
                onIncrement={() => handleSetQuantity(quantity + 1)}
                disableDelete
                size="large"
                variant="brand"
              />
            </Stack>
          )}

          {(!!selectedDefectType || lastSelectedDefectLevelValue === 'custom') && (
            <DefectsPhotosAndContexts
              defectPhotosByDefect={defectPhotosByDefect}
              setDefectPhotosByDefect={setDefectPhotosByDefect}
              mediaToDelete={mediaToDelete}
              setMediaToDelete={setMediaToDelete}
              contextByDefect={contextByDefect}
              setContextByDefect={setContextByDefect}
              quantity={quantity}
            />
          )}
        </Stack>
      </main>

      <footer>
        <ProgressBar progress={progress} />
        <Stack row gap={isMobile ? '1rem' : '2rem'}>
          {hasDefects(article) && (
            <Button
              size="large"
              variant="secondary-brand"
              style={{ flex: 1 }}
              onPress={discard}
              disabled={isUpdateDefectPending || isCreateDefectPending}
            >
              <Trans id="client.new.actions.form.cancel">Cancel</Trans>
            </Button>
          )}
          <Button
            size="large"
            variant="brand"
            style={{ flex: 1 }}
            disabled={disableSave}
            onPress={saveDefects}
            isLoading={isUpdateDefectPending || isCreateDefectPending}
          >
            {initialDefectData ? (
              <Trans id="client.new.actions.form.save">Save</Trans>
            ) : (
              <Trans id="client.new.actions.form.add-to-cart">Add to my cart</Trans>
            )}
          </Button>
        </Stack>
      </footer>
    </div>
  );
};

const DefectsPhotosAndContexts = ({
  defectPhotosByDefect,
  setDefectPhotosByDefect,
  mediaToDelete,
  setMediaToDelete,
  contextByDefect,
  setContextByDefect,
  quantity,
}: {
  defectPhotosByDefect: Medium[][];
  setDefectPhotosByDefect: (param: (defectPhotosByDefect: Medium[][]) => Medium[][]) => void;
  mediaToDelete: string[];
  setMediaToDelete: (mediaToDelete: string[]) => void;
  contextByDefect: string[];
  setContextByDefect: (param: (contexts: string[]) => string[]) => void;
  quantity: number;
}) => {
  const { t } = useLingui();
  const { view } = useBrandRequestContext();

  if (!view.articles.defects.photo.shown && !view.articles.defects.context.shown) {
    return null;
  }

  return [...new Array(quantity)].map((_x, index) => {
    const photoLabel =
      quantity > 1
        ? t({
            id: 'client.new.actions.form.photo.label.multiple',
            message: `Add photo(s) of defect #${index + 1}`,
          })
        : t({
            id: 'client.new.actions.form.photo.label.single',
            message: `Add photo(s) of the defect`,
          });

    const contextLabel =
      quantity > 1
        ? t({
            id: 'client.new.actions.form.context.label.multiple',
            message: `Defect context #${index + 1}`,
          })
        : t({
            id: 'client.new.actions.form.context.label.single',
            message: `Defect context`,
          });

    return (
      <Stack gap="1rem" key={index}>
        {view.articles.defects.photo.shown && (
          <FileUpload
            uploadData={{ type: 'defect-photo' }}
            type="photo"
            label={
              <>
                {photoLabel}
                {!view.articles.defects.photo.required && <OptionalLabel />}
              </>
            }
            ariaLabel={photoLabel}
            media={defectPhotosByDefect[index]}
            onDelete={(idToDelete) => {
              setMediaToDelete([...mediaToDelete, idToDelete]);
              setDefectPhotosByDefect((prevDefectPhotosByDefect) =>
                prevDefectPhotosByDefect.map((prevDefectPhotos, _index) =>
                  _index === index
                    ? prevDefectPhotos.filter(({ id }) => id !== idToDelete)
                    : prevDefectPhotos
                )
              );
            }}
            onUpload={(newMedium) => {
              setDefectPhotosByDefect((prevDefectPhotosByDefect) =>
                prevDefectPhotosByDefect.map((prevDefectPhotos, _index) =>
                  _index === index ? [...prevDefectPhotos, newMedium] : prevDefectPhotos
                )
              );
            }}
            size="xlarge"
            theme="brand"
            onMount={() => {
              window.scroll({
                top: document.body.scrollHeight,
                behavior: 'smooth',
              });
            }}
          />
        )}
        {view.articles.defects.context.shown && (
          <TextArea
            label={
              <>
                {contextLabel}
                {!view.articles.defects.context.required && <OptionalLabel />}
              </>
            }
            placeholder={t({
              id: 'client.new.actions.form.context.placeholder',
              message: 'Describe how the defect appeared...',
            })}
            value={contextByDefect[index]}
            onChange={(e) => {
              setContextByDefect((prevContexts) =>
                prevContexts.map((prevContext, _index) =>
                  _index === index ? e.target.value : prevContext
                )
              );
            }}
            size="large"
          />
        )}
      </Stack>
    );
  });
};

const getInitialDefectLevelValues = ({
  initialDefectData,
  defectLevelValues,
}: {
  initialDefectData: ArticleDefectWithRelations | undefined;
  defectLevelValues: DefectLevelValue[];
}): (DefectLevelValue | 'custom' | null)[] => {
  if (!initialDefectData) {
    return [null];
  }

  if (!initialDefectData.defectTypeOrganizationId) {
    return ['custom'];
  }

  const initialDefectLevelValues: DefectLevelValue[] = [];

  const addDefectLevelValueAndParent = (defectLevelValue: DefectLevelValue) => {
    initialDefectLevelValues.push(defectLevelValue);

    if (defectLevelValue.parentId) {
      const parentDefectLevelValue = defectLevelValues.find(
        (dlv) => dlv.id === defectLevelValue.parentId
      );

      if (parentDefectLevelValue) {
        addDefectLevelValueAndParent(parentDefectLevelValue);
      }
    }
  };

  const lastDefectLevelValue = defectLevelValues.find((dlv) =>
    dlv.defectTypeOrganizationIds.includes(initialDefectData.defectTypeOrganizationId!)
  );

  if (lastDefectLevelValue) {
    addDefectLevelValueAndParent(lastDefectLevelValue);
  }

  return initialDefectLevelValues.reverse();
};

export default DefectsFormWrapper;
